import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import '../styles/UserDashboard.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
// import PaymentModal from '../components/PaymentModal';
// import Certificate from '../components/Certificate';
// import QRCodeDisplay from '../components/QRCodeDisplay';
import { checkPurchaseStatus } from '../services/paymentService';
import { Link } from 'react-router-dom';

// Remove trailing slash to avoid double slashes in URLs
const API_URL = (process.env.REACT_APP_API_URL || 'https://camino-backend-iooi.onrender.com').replace(/\/$/, '');

// Default empty states
const emptyState = {
  leaderboard: [],
  completions: [],
  userRank: null
};

const UserDashboard = () => {
  const { user, isAuthenticated, isLoading, error, getAccessTokenSilently, logout } = useAuth0();
  const [hasPurchased, setHasPurchased] = useState(false);
  // const [showCertificate, setShowCertificate] = useState(false);
  // const [showQRCode, setShowQRCode] = useState(false);
  // const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  const [leaderboard, setLeaderboard] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [apiError, setApiError] = useState(null);
  
  const [timePeriod, setTimePeriod] = useState('all'); // 'all', 'month', 'week'
  const [vehicleFilter, setVehicleFilter] = useState('all'); // 'all', 'bike', 'walking', 'running', 'skating'
  const [userRank, setUserRank] = useState(null);
  
  const [lakeConfig, setLakeConfig] = useState(null);
  const [currentLakeId, setCurrentLakeId] = useState(null);
  const [currentLake, setCurrentLake] = useState(null);
  
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [confirmModalText, setConfirmModalText] = useState('');

  // Function to optimize Google profile picture URL
  const getOptimizedImageUrl = (url) => {
    if (!url) return null;
    
    // If it's a Google profile image, try to optimize it
    if (url.includes('googleusercontent.com')) {
      // Remove size restrictions and add a larger size
      const optimizedUrl = url.replace(/=s\d+-c$/, '=s200-c');
      return optimizedUrl;
    }
    
    return url;
  };

  useEffect(() => {
    if (user) {
      // User profile picture available
    }
  }, [user]);

  const fetchActivities = async () => {
    try {
      setIsLoadingData(true);
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        }
      });

      if (!token) {
        setCompletions([]);
        return;
      }
      
      // Fetch user's own entries using the new endpoint (all lakes)
      const response = await fetch(`${API_URL}/api/user/entries`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
      });
      
      if (!response.ok) {
        setCompletions([]);
        return;
      }
      
      const data = await response.json();

      if (data.success && data.entries && Array.isArray(data.entries)) {
        // Map user entries to the expected format
        const userCompletions = data.entries.map((entry) => {
          return {
            id: entry._id,
            route: `${entry.lakeId?.displayName || entry.lakeId?.name || 'Tó'} Körút`,
            date: new Date(entry.completedAt).toISOString().split("T")[0],
            distance: ((entry.totalDistanceMeters || 0) / 1000).toFixed(1),
            time: formatDuration(entry.totalActiveSeconds || 0),
            medals: [], // Medals not included in leaderboard entries
            routeProgress: 100, // Leaderboard entries are 100% complete
            vehicle: entry.vehicle || "Kerékpár"
          };
        });
        
        // Already sorted by completedAt desc from backend
        setActivities(userCompletions);
        setCompletions(userCompletions);
      } else {
        // No entries found
        setCompletions([]);
        setActivities([]);
      }
    } catch (error) {
      console.error("Error fetching user activities:", error);
      setCompletions([]);
      setActivities([]);
    } finally {
      setIsLoadingData(false);
    }
  };
  
  const formatDuration = (seconds) => {
    if (!seconds) return "0";
    
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const ss = String(s).padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
  };
  
  const getVehicleName = (vehicle) => {
    switch (vehicle) {
      case 'bike':
        return 'Kerékpár';
      case 'walking':
        return 'Gyaloglás';
      case 'running':
        return 'Futás';
      case 'skating':
        return 'Roller';
      default:
        return vehicle || 'Ismeretlen';
    }
  };

  const getDisplayValue = (item) => {
    return formatDuration(item.duration || 0);
  };
  
  const deleteActivity = async (activityId) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        }
      });

      if (!token) {
        setErrorMessage("Nem található belépési token");
        setShowErrorModal(true);
        return false;
      }
      
      // Delete the user's leaderboard entry
      const response = await fetch(`${API_URL}/api/user/entries/${activityId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server responded with ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Remove the deleted item from the local state
        setActivities(prev => prev.filter(activity => activity.id !== activityId));
        setCompletions(prev => prev.filter(activity => activity.id !== activityId));
        
        // Refresh leaderboard to update rankings after deletion
        try {
          await fetchLeaderboard();
        } catch (leaderboardError) {
          console.warn("Failed to refresh leaderboard after deletion:", leaderboardError);
          // Don't fail the deletion if leaderboard refresh fails
        }
        
        // Show success message
        setSuccessMessage("Teljesítmény sikeresen törölve");
        setShowSuccessModal(true);
        
        return true;
      } else {
        throw new Error(data.message || "Törlés sikertelen");
      }
    } catch (error) {
      console.error("Error deleting activity:", error);
      setErrorMessage(`Nem sikerült törölni a teljesítményt: ${error.message}`);
      setShowErrorModal(true);
      return false;
    }
  };
  
  const fetchLeaderboard = async () => {
    try {
      let token;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE
          }
        });
      } catch (tokenError) {
        setLeaderboard([]);
        return;
      }

      if (!token) {
        setLeaderboard([]);
        return;
      }
      
      if (!currentLakeId) {
        setLeaderboard([]);
        return;
      }
      
      // Use NEW leaderboard API endpoint
      const params = new URLSearchParams({
        lakeId: currentLakeId,
        timePeriod: timePeriod, // 'all', 'month', 'week'
        vehicle: vehicleFilter, // 'all', 'bike', 'walking', 'running', 'skating'
        limit: '50'
      });
      
      const url = `${API_URL}/api/leaderboard?${params}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Leaderboard API error:", {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      if (data.leaderboard && Array.isArray(data.leaderboard)) {
        setLeaderboard(data.leaderboard);
        
        if (data.userRank) {
          setUserRank(data.userRank);
        } else {
          setUserRank(null);
        }
      } else {
        console.warn("⚠️ Invalid leaderboard data format:", data);
        setLeaderboard([]);
      }
    } catch (error) {
      console.error("❌ Error fetching leaderboard:", error.message);
    }
  };
  
  useEffect(() => {
    if (isAuthenticated && user && currentLakeId) {
      // Leaderboard filters changed, refetching
      fetchLeaderboard();
    }
  }, [timePeriod, vehicleFilter, isAuthenticated, user, currentLakeId]);

  const fetchPurchaseStatus = async () => {
    try {
      const token = await getAccessTokenSilently();
      
      if (!token) {
        // No auth token available for purchase status
        return;
      }
      
      const auth0Id = user.sub;
      //console.log(token, auth0Id);
      const { hasPurchased } = await checkPurchaseStatus(token, auth0Id);
      setHasPurchased(hasPurchased);
      
    } catch (error) {
      console.error("Error checking purchase status:", error);
      setHasPurchased(localStorage.getItem('hasPurchased') === 'true');
    }
  };

  // Fetch user's selected lake from their profile
  const fetchUserSelectedLake = async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        }
      });

      if (!token) {
        console.log('❌ No token for user profile');
        return;
      }

      // Fetch user profile to get their selected lake
      const userResponse = await fetch(`${API_URL}/api/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        console.log('👤 User profile:', userData);
        
        if (userData.user?.selectedLakeId) {
          setCurrentLakeId(userData.user.selectedLakeId);
          console.log('✅ Using user selected lake:', userData.user.selectedLakeId);
        } else {
          // User hasn't selected a lake yet, fetch available lakes
          const lakesResponse = await fetch(`${API_URL}/api/lakes`);
          if (lakesResponse.ok) {
            const lakesData = await lakesResponse.json();
            const firstLakeId = lakesData.lakes?.[0]?._id || '688f58e154c5905fc7a678a9';
            setCurrentLakeId(firstLakeId);
            console.log('⚠️ No selected lake, using first:', firstLakeId);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user lake:', error);
      // Default to Tisza lake ID if fetch fails
      const fallbackLakeId = '688f58e154c5905fc7a678a9';
      setCurrentLakeId(fallbackLakeId);
    }
  };

  // Fetch current lake details
  const fetchCurrentLake = async (lakeId) => {
    try {
      const response = await fetch(`${API_URL}/api/lakes`);
      if (response.ok) {
        const data = await response.json();
        const lake = data.lakes?.find(l => l._id === lakeId);
        setCurrentLake(lake || null);
      }
    } catch (error) {
      console.error('Error fetching lake details:', error);
      setCurrentLake(null);
    }
  };

  useEffect(() => {
    // Fetch user's selected lake when authenticated
    if (isAuthenticated && user) {
      fetchUserSelectedLake();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    // console.log('📅 Data fetch useEffect triggered:', { isAuthenticated, userExists: !!user, currentLakeId });

    if (isAuthenticated && user && currentLakeId) {
      const fetchData = async () => {
        // console.log('🔄 Starting data fetch...');
        setIsLoadingData(true);
        try {
          await fetchActivities();
          await fetchLeaderboard();
          await fetchPurchaseStatus();
          // console.log('✅ All data fetched successfully');
        } catch (err) {
          console.error('❌ Error fetching data:', err.message);
          setApiError(`Nem sikerült betölteni az adatokat: ${err.message}`);
        } finally {
          setIsLoadingData(false);
        }
      };

      fetchData();
    } else {
      // console.log('⏸️ Skipping data fetch - missing requirements');
    }
  }, [isAuthenticated, user, currentLakeId]);

  useEffect(() => {
    // Fetch lake details when currentLakeId changes
    if (currentLakeId) {
      fetchCurrentLake(currentLakeId);
    }
  }, [currentLakeId]);

  // const handlePurchase = () => {
  //   setShowPaymentModal(true);
  // };

  // const handlePaymentSuccess = () => {
  //   setShowPaymentModal(false);
  //   setHasPurchased(true);
  //   localStorage.setItem('hasPurchased', 'true');
  // };

  // const generateCertificate = () => {
  //   setShowCertificate(true);
  // };

  // const generateQRCode = () => {
  //   setShowQRCode(true);
  // };

  const handleDeleteActivity = (activityId) => {
    setActivityToDelete(activityId);
    setConfirmModalText('Biztosan törölni szeretnéd ezt a tevékenységet?');
    setShowConfirmModal(true);
  };
  
  const confirmDelete = () => {
    if (activityToDelete) {
      deleteActivity(activityToDelete);
      setShowConfirmModal(false);
      setActivityToDelete(null);
    }
  };

  // const getFilterName = (filterValue) => {
  //   switch (filterValue) {
  //     case 'bike':
  //       return 'Kerékpár';
  //     case 'walking':
  //       return 'Gyaloglás';
  //     case 'running':
  //       return 'Futás';
  //     case 'skating':
  //       return 'Roller';
  //     default:
  //       return 'Összes';
  //   }
  // };

  const getSortName = (sortValue) => {
    switch (sortValue) {
      case 'speed':
        return 'Sebesség';
      case 'time':
        return 'Idő';
      default:
        return 'Idő';
    }
  };

  if (isLoading) {
    return <div className="loading">Profil adatok betöltése...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <h2>Hitelesítési hiba</h2>
        <p>Hiba típusa: {error.name}</p>
        <p>Üzenet: {error.message}</p>
        <p>Kód: {error.error || "N/A"}</p>
        <p>Leírás: {error.error_description || "N/A"}</p>
        <button 
          onClick={() => window.location.href='/'}
          className="btn-primary"
        >
          Vissza a Főoldalra
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <div className="dashboard-container">Kérjük, jelentkezzen be a profil megtekintéséhez.</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-inner-container">
        <div className="profile-section">
          <div className="profile-header">
            {user.picture ? (
              <img 
                src={getOptimizedImageUrl(user.picture)} 
                alt="Profil" 
                className="profile-picture" 
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                onLoad={(e) => {
                  e.target.style.display = 'block';
                  e.target.nextSibling.style.display = 'none';
                }}
                onError={(e) => {
                  // Profile picture failed to load, switching to fallback
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="profile-picture-fallback"
              style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                backgroundColor: '#049A63',
                display: user.picture ? 'none' : 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                fontSize: '2rem',
                fontWeight: '600',
                border: '4px solid white',
                boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)'
              }}
            >
              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <div className="profile-info">
              <h2>{user.name}</h2>
              <p>{user.email}</p>
              <div className="profile-actions">
                <button 
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                  className="logout-button"
                >
                  Kijelentkezés
                </button>
                <Link to="/delete-account" className="delete-account-button-small"> 
                  Fiók törlése
                </Link>
              </div>
            </div>
          </div>
        </div>

      {isLoadingData ? (
        <div className="loading">Adatok betöltése...</div>
      ) : apiError ? (
        <div className="api-error">{apiError}</div>
      ) : (
        <>
          <div className="dashboard-grid">
            <div className="grid-item completions-section">
              <h3>Korábbi Teljesítések</h3>
              {completions.length > 0 ? (
                <div className="completions-list">
                  {completions.map(completion => (
                    <div key={completion.id} className="completion-card">
                      <div className="completion-header">
                        <h4>{completion.route}</h4>
                        <span className="completion-date">{completion.date}</span>
                      </div>
                      <div className="completion-details">
                        <span><i className="fas fa-route"></i> Távolság: {completion.distance} km</span>
                        <span><i className="fas fa-clock"></i> Idő: {completion.time}</span>
                        <button 
                          className="delete-button" 
                          onClick={() => handleDeleteActivity(completion.id)}
                        >
                        Törlés
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-message">Még nincsenek korábbi teljesítések</p>
              )}
            </div>
          </div>

          <div className="leaderboard-section">
            <h3>Ranglista{currentLake ? ` - ${currentLake.displayName}` : ''}</h3>
            <div className="filter-container">
              {/* Time Period Filter */}
              <div className="filter-section">
                <span className="filter-label">
                  <i className="fas fa-calendar" style={{marginRight: '6px'}}></i>
                  Időszak:
                </span>
                <div className="filter-chips">
                  <button 
                    className={`filter-chip ${timePeriod === 'all' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('all')}
                  >
                    Minden idők
                  </button>
                  <button 
                    className={`filter-chip ${timePeriod === 'month' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('month')}
                  >
                    Havi
                  </button>
                  <button 
                    className={`filter-chip ${timePeriod === 'week' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('week')}
                  >
                    Heti
                  </button>
                </div>
              </div>

              {/* Vehicle Filter */}
              <div className="filter-section">
                <span className="filter-label">
                  <i className="fas fa-award" style={{marginRight: '6px'}}></i>
                  Jármű:
                </span>
                <div className="filter-chips">
                  <button 
                    className={`filter-chip ${vehicleFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setVehicleFilter('all')}
                  >
                    Mind
                  </button>
                  <button 
                    className={`filter-chip ${vehicleFilter === 'bike' ? 'active' : ''}`}
                    onClick={() => setVehicleFilter('bike')}
                  >
                    <i className="fas fa-biking" style={{marginRight: '6px'}}></i>
                    Kerékpár
                  </button>
                  <button 
                    className={`filter-chip ${vehicleFilter === 'walking' ? 'active' : ''}`}
                    onClick={() => setVehicleFilter('walking')}
                  >
                    <i className="fas fa-walking" style={{marginRight: '6px'}}></i>
                    Gyaloglás
                  </button>
                  <button 
                    className={`filter-chip ${vehicleFilter === 'running' ? 'active' : ''}`}
                    onClick={() => setVehicleFilter('running')}
                  >
                    <i className="fas fa-running" style={{marginRight: '6px'}}></i>
                    Futás
                  </button>
                  <button 
                    className={`filter-chip ${vehicleFilter === 'skating' ? 'active' : ''}`}
                    onClick={() => setVehicleFilter('skating')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{marginRight: '6px'}}>
                      <path d="M7.82 16H15v-1c0-2.21 1.79-4 4-4h.74l-1.9-8.44A2.01 2.01 0 0 0 15.89 1H12v2h3.89l1.4 6.25h-.01A6.01 6.01 0 0 0 13.09 14H7.82a2.996 2.996 0 0 0-3.42-1.94c-1.18.23-2.13 1.2-2.35 2.38A3.002 3.002 0 0 0 5 18c1.3 0 2.4-.84 2.82-2M5 16c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1" fill={vehicleFilter === 'skating' ? '#ffffff' : '#666'} />
                      <path d="M19 12c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3m0 4c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1m-8 4H7l6 3v-2h4l-6-3z" fill={vehicleFilter === 'skating' ? '#ffffff' : '#666'} />
                    </svg>
                    Roller
                  </button>
                </div>
              </div>
            </div>
            
            {isLoadingData ? (
              <div className="loading-container-small">
                <div className="loading-spinner"></div>
                <p>Betöltés...</p>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="empty-container">
                <i className="fas fa-trophy empty-trophy"></i>
                <p className="empty-text">Nincs még adat ebben a kategóriában</p>
              </div>
            ) : (
              <>
                {/* Podium for Top 3 */}
                {leaderboard.length >= 3 && (
                  <div className="podium-container">
                    <h4 className="podium-title">Dobogó</h4>
                    <div className="podium-row">
                      {/* 2nd Place */}
                      {leaderboard[1] && (
                        <div className="podium-item second-place" data-rank="2">
                          <div className="podium-rank-badge silver-badge">#2</div>
                          <div className="podium-user-name">{leaderboard[1].userName || 'Felhasználó'}</div>
                          <div className="podium-stats">
                            <div className="podium-time">
                              <i className="fas fa-clock" style={{marginRight: '4px', fontSize: '0.85rem'}}></i>
                              {formatDuration(leaderboard[1].totalActiveSeconds || 0)}
                            </div>
                            <div className="podium-speed">
                              <i className="fas fa-bolt" style={{marginRight: '4px', fontSize: '0.7rem'}}></i>
                              {leaderboard[1].avgSpeedKmh || '-'} km/h
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* 1st Place */}
                      {leaderboard[0] && (
                        <div className="podium-item first-place" data-rank="1">
                          <div className="podium-rank-badge gold-badge">#1</div>
                          <div className="podium-user-name">{leaderboard[0].userName || 'Felhasználó'}</div>
                          <div className="podium-stats">
                            <div className="podium-time">
                              <i className="fas fa-clock" style={{marginRight: '4px', fontSize: '0.85rem'}}></i>
                              {formatDuration(leaderboard[0].totalActiveSeconds || 0)}
                            </div>
                            <div className="podium-speed">
                              <i className="fas fa-bolt" style={{marginRight: '4px', fontSize: '0.7rem'}}></i>
                              {leaderboard[0].avgSpeedKmh || '-'} km/h
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* 3rd Place */}
                      {leaderboard[2] && (
                        <div className="podium-item third-place" data-rank="3">
                          <div className="podium-rank-badge bronze-badge">#3</div>
                          <div className="podium-user-name">{leaderboard[2].userName || 'Felhasználó'}</div>
                          <div className="podium-stats">
                            <div className="podium-time">
                              <i className="fas fa-clock" style={{marginRight: '4px', fontSize: '0.85rem'}}></i>
                              {formatDuration(leaderboard[2].totalActiveSeconds || 0)}
                            </div>
                            <div className="podium-speed">
                              <i className="fas fa-bolt" style={{marginRight: '4px', fontSize: '0.7rem'}}></i>
                              {leaderboard[2].avgSpeedKmh || '-'} km/h
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* User Rank Card (if not in top 3) */}
                {userRank && userRank.rank > 3 && (
                  <div className="user-rank-container">
                    <div className="user-rank-header">
                      <i className="fas fa-trophy" style={{color: '#049A63', marginRight: '8px'}}></i>
                      <p className="user-rank-text">Az Ön helyezése: #{userRank.rank}</p>
                    </div>
                    <div className="user-rank-stats">
                      <div className="user-rank-stat">
                        <i className="fas fa-clock" style={{fontSize: '14px', color: '#666', marginRight: '4px'}}></i>
                        <span>{formatDuration(userRank.totalActiveSeconds || 0)}</span>
                      </div>
                      <div className="user-rank-stat">
                        <i className="fas fa-bolt" style={{fontSize: '14px', color: '#049A63', marginRight: '4px'}}></i>
                        <span style={{color: '#049A63', fontWeight: '600'}}>{userRank.avgSpeedKmh || '-'} km/h</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Remaining Entries */}
                {leaderboard.length > 3 && (
                  <>
                    <h4 className="remaining-header">További helyezések</h4>
                    <div className="leaderboard-list">
                      {leaderboard.slice(3).map((entry, index) => {
                        const actualRank = index + 4; // Starts at 4
                        const isCurrentUser = userRank && userRank.rank === actualRank;
                        
                        return (
                          <div 
                            key={entry._id} 
                            className={`leaderboard-item ${isCurrentUser ? 'current-user-item' : ''}`}
                          >
                            <div className="rank-container">
                              <span className="rank-text">#{actualRank}</span>
                            </div>
                            
                            <div className="user-info">
                              <div className="user-avatar">
                                {entry.userInfo?.[0]?.picture ? (
                                  <img src={entry.userInfo[0].picture} alt={entry.userName} className="user-picture" />
                                ) : (
                                  <div className="default-avatar">
                                    <span>{entry.userName ? entry.userName[0].toUpperCase() : '?'}</span>
                                  </div>
                                )}
                              </div>
                              
                              <div className="user-details">
                                <div className="user-name">{entry.userName || 'Felhasználó'}</div>
                                <div className="user-activity">{entry.vehicle}</div>
                              </div>
                            </div>
                            
                            <div className="stats-container">
                              <div className="stat-row">
                                <i className="fas fa-clock" style={{fontSize: '14px', color: '#666', marginRight: '4px'}}></i>
                                <span className="stat-value">{formatDuration(entry.totalActiveSeconds || 0)}</span>
                              </div>
                              <div className="stat-row">
                                <i className="fas fa-bolt" style={{fontSize: '14px', color: '#049A63', marginRight: '4px'}}></i>
                                <span className="speed-value">{entry.avgSpeedKmh || '-'} km/h</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/*
      {!hasPurchased ? (
        <div className="package-section">
          <h3>Elérhető Csomag</h3>
          <div className="single-package">
            <div className="package-card highlighted">
              <h4>Camino Tisza-tó Csomag</h4>
              <p className="price">1000 Ft</p>
              <ul className="features-list">
                <li>Hivatalos Camino Tisza-tó Oklevél</li>
                <li>QR kód 2 gombóc fagyihoz</li>
                <li>Személyre szabott élmény</li>
              </ul>
              <button className="purchase-button" onClick={handlePurchase}>Vásárlás</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="purchased-section">
          <h3>Megvásárolt Csomag</h3>
          <div className="purchased-package">
            <div className="package-info">
              <h4>Camino Tisza-tó Csomag</h4>
              <p>Vásárlás dátuma: {new Date().toLocaleDateString('hu-HU')}</p>
              <p>Státusz: <span className="status-active">Aktív</span></p>
            </div>
            <div className="package-actions">
              <button className="action-button" onClick={generateCertificate}>
                Oklevél Letöltése
              </button>
              <button className="action-button" onClick={generateQRCode}>
                Fagyi QR Kód Mutatása
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && (
        <PaymentModal 
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
          amount={1000}
          auth0Id={user.sub}
        />
      )}

      {showCertificate && (
        <Certificate onClose={() => setShowCertificate(false)} />
      )}

      {showQRCode && (
        <QRCodeDisplay onClose={() => setShowQRCode(false)} />
      )}
        */}

      {showErrorModal && (
        <div className="modal-container">
          <div className="modal-content">
            <h3>Hiba történt</h3>
            <p>{errorMessage}</p>
            <button 
              className="modal-button" 
              onClick={() => setShowErrorModal(false)}
            >
              Bezárás
            </button>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modal-container">
          <div className="modal-content">
            <h3 style={{color: '#049A63'}}>Sikeres művelet</h3>
            <p>{successMessage}</p>
            <button 
              className="modal-button" 
              style={{backgroundColor: '#049A63'}}
              onClick={() => setShowSuccessModal(false)}
            >
              Rendben
            </button>
          </div>
        </div>
      )}
      </div>
      
      {showConfirmModal && (
        <div className="modal-container">
          <div className="modal-content">
            <h3 className="confirm-title">Megerősítés</h3>
            <p>{confirmModalText}</p>
            <div className="modal-actions">
              <button 
                className="modal-button confirm-button" 
                onClick={confirmDelete}
              >
                Igen
              </button>
              <button 
                className="modal-button cancel-button" 
                onClick={() => setShowConfirmModal(false)}
              >
                Mégsem
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;